import React, { useEffect, useState, useContext } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
} from '@mui/material';
import { styled as muiStyled } from "@mui/system";
import MoreVert from '@mui/icons-material/MoreVert';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

// Deine Stores & Configs
import { ThemeContext } from '../stores/ThemeContext';
import { useCommentOverlayStore } from '../stores/CommentStore';
import { useGeneralInfoStore } from '../stores/GeneralInfoStore';
import { enableDownloadPdf, logoutUrl } from '../config/config';
import appRoutesConfig from '../config/appConfig';

// Keycloak Context
import { KeycloakContext } from '../components/Keycloak/keycloakContext';

// Dein Logo
import OrganizationLogo from './componets/header/OrganizationLogo';

// Optional: CommentsOverlay, falls du den Overlay-Button doch behalten willst
import CommentsOverlay from './CommentsOverlay';
import SettingsDialog from './componets/header/SettingsDialog';
import Logout from '../assets/icons/demoIcons/logout';
import Settings from '../assets/icons/settings';
import DownloadPageButton from './componets/header/DownloadPageButton';
import { categoryTabs } from '../config/categoryTabsCofig';
import { useLocation } from 'react-router-dom';

// -----------------------------------
// Styled Components
// -----------------------------------
const Divider = styled.div`
  margin: 0.5rem;
  margin-top: 1.5rem;
  width: 1px;
  height: 2rem;
  background-color: ${props => props.theme.palette.customText.headingsGrayedOut};
`;

const MyAppBar = muiStyled(AppBar)(({ theme }) => ({
    maxHeight: '48px !important',
    height: '48px !important',
    width: '100% !important',
    WebkitFillAvailable: 'fill-available !important',
    MozAvailable: 'available !important',
    paddingLeft: '1rem !important',
    paddingRight: '1rem !important',
    borderRadius: '0px !important',
    border: "none !important",
}));

const MyToolbar = muiStyled(Toolbar)({
    display: 'flex',
    justifyContent: 'space-between',
    height: "32px",
    maxHeight: '32px !important',
    minHeight: '32px !important',
    padding: '0px !important',
});

const MyIconButton = muiStyled(IconButton)({
    height: '38px',
    width: '38px',
    color: 'black',
    marginTop: '12px',
});

// -----------------------------------
// Header Component
// -----------------------------------
const Header: React.FC = () => {
    // State für die Uhrzeit
    const [currentTime, setCurrentTime] = useState<Date>(new Date());

    // Theme, um Farben zu laden
    const theme = useTheme();

    // Keycloak Context
    const { logout } = useContext(KeycloakContext);

    // Hier behältst du deine Farb-Referenzen bei!
    const checkedColor = theme.palette.switch.checked;
    const trackColor = theme.palette.switch.track;
    const iconColor = theme.palette.switch.icon;
    const headerColor = theme.palette.background.sidebarBackground;
    const textColor = theme.palette.text.primary;
    const { pathname } = useLocation();

    // Stores & Config
    const { activeTab, category } = useGeneralInfoStore();
    const showCommentSection = appRoutesConfig.Options.CommentSection;

    // i18n
    const { t } = useTranslation();

    // Comments Overlay (falls du es brauchst)
    const isOverlayOpen = useCommentOverlayStore((state) => state.isOverlayOpen);
    const toggleCommentsOverlay = () => {
        useCommentOverlayStore.getState().setIsOverlayOpen(!isOverlayOpen);
    };

    // State für das Settings-Popup
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Lifecycle für Uhrzeit-Update
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    // Zeit formatieren
    const formatTime = (date: Date) => {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    // Datum formatieren
    const formatDate = (date: Date) => {
        const days = [
            t('dates.weekdays.sunday'),
            t('dates.weekdays.monday'),
            t('dates.weekdays.tuesday'),
            t('dates.weekdays.wednesday'),
            t('dates.weekdays.thursday'),
            t('dates.weekdays.friday'),
            t('dates.weekdays.saturday'),
        ];
        const months = [
            t('dates.months.january'),
            t('dates.months.february'),
            t('dates.months.march'),
            t('dates.months.april'),
            t('dates.months.may'),
            t('dates.months.june'),
            t('dates.months.july'),
            t('dates.months.august'),
            t('dates.months.september'),
            t('dates.months.october'),
            t('dates.months.november'),
            t('dates.months.december'),
        ];

        const dayOfWeek = days[date.getDay()];
        const dayOfMonth = date.getDate();
        const month = months[date.getMonth()];

        return `${dayOfWeek}, ${dayOfMonth}. ${month}`;
    };

    // SettingsDialog ein-/ausblenden
    const openSettings = () => setSettingsOpen(true);
    const closeSettings = () => setSettingsOpen(false);

    const getTranslatedTabTitle = () => {
        // don't show on landing or catalogue
        if (pathname === '/Landing' || pathname === '/Catalogue' || pathname === '/EventManager' || pathname === '/EventManager' || !activeTab) {
            return '';
        }

        for (const group of Object.values(categoryTabs)) {
            const found = group.find(tab => tab.name === activeTab);
            if (found) return t(found.title);
        }
        return activeTab;
    };

    // Keycloak Logout Handler
    const handleLogout = async () => {
        try {
            if (logout) {
                await logout();
            } else {
                // Fallback to the original logout URL if Keycloak logout is not available
                window.location.href = logoutUrl;
            }
        } catch (error) {
            console.error('Logout failed:', error);
            // Fallback to the original logout URL on error
            window.location.href = logoutUrl;
        }
    };

    return (
        <MyAppBar position="static" sx={{ bgcolor: headerColor }}>
            <MyToolbar>

                {/* LINKS: Uhrzeit, Datum, Seite */}
                <Typography
                    color={textColor}
                    sx={{
                        marginTop: '12px',
                        flexGrow: 1,
                        fontStyle: 'normal',
                        fontWeight: '300',
                        fontSize: '26px',
                        lineHeight: '39px',
                    }}
                >
                    {formatTime(currentTime)}| {formatDate(currentTime)}
                    {activeTab ? ` | ${getTranslatedTabTitle()}` : ""}
                </Typography>

                {/* RECHTS: Logo + Button zum Öffnen des Settings-Popups */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '1rem',
                }}>
                    {enableDownloadPdf && (
                        <>
                            <DownloadPageButton />
                            <Divider />
                        </>
                    )}
                    {/* EIN KNOPF: Öffnet das neue Settings-Popup */}
                    <MyIconButton
                        onClick={openSettings}
                        aria-label="Open Settings"
                        sx={{ marginTop: '16px' }}
                    >
                        <Settings color={textColor} size={20} />
                    </MyIconButton>
                    <Divider />
                    <div style={{ marginTop: '16px', marginRight: '-1rem' }}>
                        <OrganizationLogo />
                    </div>
                    <Divider />

                    <div
                        onClick={handleLogout}
                        style={{ cursor: 'pointer' }}
                    >
                        <span style={{ marginTop: '16px', display: 'flex' }}>
                            <Logout size={16} color={textColor} />
                        </span>
                    </div>
                </div>
            </MyToolbar>

            {/* Das neue SETTINGS-Pop-up */}
            <SettingsDialog
                open={settingsOpen}
                onClose={closeSettings}

                // Hier übergeben wir z. B. die Theme-Farben,
                // damit der Dialog sie nutzen kann.
                checkedColor={checkedColor}
                trackColor={trackColor}
                iconColor={iconColor}
                textColor={textColor}
            />
            <CommentsOverlay open={isOverlayOpen} onClose={toggleCommentsOverlay} />
        </MyAppBar>
    );
};

export default Header;